import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useUserCheck } from '../../hooks/api/useUserCheck'
import { useDevicesLive } from '../../hooks/api/useDevicesLive'
import { useUpdateDevice, useDeleteDevice } from '../../hooks/api/useDevices'
import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { LoadingState } from '../../components/ui/LoadingState'
import { ErrorState } from '../../components/ui/ErrorState'
import { Pagination } from '../../components/ui/Pagination'
import { Select } from '../../components/ui/Select'
import type { Device } from '../../services/devices'

type StatusFilter = 'all' | 'online' | 'offline' | 'warning' | 'error'

export const AdminDevicesPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data: userRecord, isLoading: userLoading, error: userError } = useUserCheck(user?.id || '')
  const { data: devices = [], isLoading: devicesLoading, error: devicesError } = useDevicesLive()
  const updateDeviceMutation = useUpdateDevice()
  const deleteDeviceMutation = useDeleteDevice()

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [organizationFilter, setOrganizationFilter] = useState<string>('all')
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('all')
  const [onlyAssigned, setOnlyAssigned] = useState(false)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false)
  const bulkMenuRef = useRef<HTMLDivElement | null>(null)
  const selectAllRef = useRef<HTMLInputElement | null>(null)
  const [bulkActionLoading, setBulkActionLoading] = useState(false)
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const formattedDevices = useMemo(() => {
    // Map devices to display format (from DeviceLive)
    return devices.map(device => ({
      id: device.id,
      deviceId: device.kaseya_device_id?.slice(0, 8) || device.id.slice(0, 8),
      name: device.name,
      organization: device.company_name || device.organization_name || 'Unknown',
      status: device.status === 'unknown' ? 'offline' : device.status,
      email: '-', // Owner info not available from Kaseya cache
      deviceType: device.group_name || 'Unknown',
      lastActive: device.last_synced_at
        ? new Date(device.last_synced_at).toLocaleDateString()
        : '-',
      tickets: device.ticket_count || 0
    }))
  }, [devices])

  const organizationOptions = useMemo(() => {
    const unique = new Set<string>()
    formattedDevices.forEach(device => {
      if (device.organization) unique.add(device.organization)
    })
    return Array.from(unique).sort()
  }, [formattedDevices])

  const deviceTypeOptions = useMemo(() => {
    const unique = new Set<string>()
    formattedDevices.forEach(device => {
      if (device.deviceType) unique.add(device.deviceType)
    })
    return Array.from(unique).sort()
  }, [formattedDevices])

  const filteredDevices = useMemo(() => {
    return formattedDevices.filter(device => {
      const matchesSearch =
        device.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.deviceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.organization.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesStatus = statusFilter === 'all' || device.status === statusFilter
      const matchesOrganization = organizationFilter === 'all' || device.organization === organizationFilter
      const matchesDeviceType = deviceTypeFilter === 'all' || device.deviceType === deviceTypeFilter
      const matchesAssigned = !onlyAssigned || (device.email && device.email !== '-')

      return matchesSearch && matchesStatus && matchesOrganization && matchesDeviceType && matchesAssigned
    })
  }, [formattedDevices, searchTerm, statusFilter, organizationFilter, deviceTypeFilter, onlyAssigned])

  const totalDevices = formattedDevices.length
  const onlineDevices = formattedDevices.filter(device => device.status === 'online').length
  const offlineDevices = formattedDevices.filter(device => device.status === 'offline').length
  const assignedDevices = formattedDevices.filter(device => device.email && device.email !== '-').length
  const selectedCount = selectedDeviceIds.length

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredDevices.length / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedDevices = useMemo(
    () => filteredDevices.slice(startIndex, startIndex + itemsPerPage),
    [filteredDevices, startIndex, itemsPerPage]
  )

  const allVisibleSelected =
    paginatedDevices.length > 0 && paginatedDevices.every(device => selectedDeviceIds.includes(device.id))

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter, organizationFilter, deviceTypeFilter, onlyAssigned])

  const handleViewDevice = (deviceId: string) => {
    navigate(`/devices/${deviceId}`)
  }

  useEffect(() => {
    setSelectedDeviceIds(prev => {
      const filtered = prev.filter(id => formattedDevices.some(device => device.id === id))
      // Only update if the filtered array is actually different
      if (filtered.length !== prev.length || !filtered.every((id, idx) => id === prev[idx])) {
        return filtered
      }
      return prev
    })
  }, [formattedDevices])

  useEffect(() => {
    if (!bulkMenuOpen) return
    const handleClickAway = (event: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(event.target as Node)) {
        setBulkMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickAway)
    return () => document.removeEventListener('mousedown', handleClickAway)
  }, [bulkMenuOpen])

  useEffect(() => {
    if (!actionFeedback) return
    const timeout = setTimeout(() => setActionFeedback(null), 5000)
    return () => clearTimeout(timeout)
  }, [actionFeedback])

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allVisibleSelected
    }
  }, [selectedCount, allVisibleSelected])

  // Devices now loaded from cache via useDevicesLive hook

  const handleToggleDeviceSelection = (deviceId: string) => {
    setSelectedDeviceIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId]
    )
  }

  const handleToggleSelectAll = () => {
    const visibleIds = paginatedDevices.map(device => device.id)
    const areAllSelected = visibleIds.every(id => selectedDeviceIds.includes(id))
    if (areAllSelected) {
      setSelectedDeviceIds(prev => prev.filter(id => !visibleIds.includes(id)))
    } else {
      const newSelection = new Set(selectedDeviceIds)
      visibleIds.forEach(id => newSelection.add(id))
      setSelectedDeviceIds(Array.from(newSelection))
    }
  }

  const handleBulkStatusChange = async (newStatus: Device['status']) => {
    if (!selectedDeviceIds.length) {
      setActionFeedback({ type: 'error', message: 'Select at least one device to run a bulk action.' })
      setBulkMenuOpen(false)
      return
    }

    setBulkActionLoading(true)
    try {
      await Promise.all(
        selectedDeviceIds.map(deviceId =>
          updateDeviceMutation.mutateAsync({
            deviceId,
            updates: { status: newStatus }
          })
        )
      )
      setActionFeedback({
        type: 'success',
        message: `Updated ${selectedDeviceIds.length} device${selectedDeviceIds.length === 1 ? '' : 's'} to ${newStatus}.`
      })
      setSelectedDeviceIds([])
    } catch (error) {
      console.error('AdminDevicesPage: bulk status update failed', error)
      setActionFeedback({ type: 'error', message: 'Failed to update device status. Please try again.' })
    } finally {
      setBulkActionLoading(false)
      setBulkMenuOpen(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!selectedDeviceIds.length) {
      setActionFeedback({ type: 'error', message: 'Select at least one device to delete.' })
      setBulkMenuOpen(false)
      return
    }

    const confirmed = window.confirm(
      `Delete ${selectedDeviceIds.length} device${selectedDeviceIds.length === 1 ? '' : 's'}? This cannot be undone.`
    )
    if (!confirmed) return

    setBulkActionLoading(true)
    try {
      await Promise.all(selectedDeviceIds.map(deviceId => deleteDeviceMutation.mutateAsync(deviceId)))
      setActionFeedback({
        type: 'success',
        message: `Deleted ${selectedDeviceIds.length} device${selectedDeviceIds.length === 1 ? '' : 's'}.`
      })
      setSelectedDeviceIds([])
    } catch (error) {
      console.error('AdminDevicesPage: bulk delete failed', error)
      setActionFeedback({ type: 'error', message: 'Failed to delete devices. Please try again.' })
    } finally {
      setBulkActionLoading(false)
      setBulkMenuOpen(false)
    }
  }

  const handleExportCsv = () => {
    if (!filteredDevices.length) {
      setActionFeedback({ type: 'error', message: 'No devices match the current filters to export.' })
      return
    }

    const headers = [
      'Device ID',
      'Name',
      'Organization',
      'Status',
      'Owner Email',
      'Device Type',
      'Last Active',
      'Open Tickets'
    ]

    const rows = filteredDevices.map(device => [
      device.deviceId,
      device.name,
      device.organization,
      device.status,
      device.email || '-',
      device.deviceType || '-',
      device.lastActive || '-',
      device.tickets ?? 0
    ])

    const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => escapeCell(String(cell ?? ''))).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `devices-${new Date().toISOString()}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    setActionFeedback({
      type: 'success',
      message: `Exported ${filteredDevices.length} device${filteredDevices.length === 1 ? '' : 's'} to CSV.`
    })
  }

  if (userLoading || devicesLoading) {
    return <LoadingState message="Loading devices..." />
  }

  if (userError || !userRecord || devicesError) {
    return <ErrorState title="Unable to load devices." description="Please try refreshing the page." />
  }


  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Devices</h1>
            <p className="mt-1 text-sm text-gray-500">Monitor hardware health and assignments across all organizations.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative" ref={bulkMenuRef}>
              <button
                type="button"
                onClick={() => setBulkMenuOpen(prev => !prev)}
                disabled={bulkActionLoading}
                className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {bulkActionLoading ? 'Working…' : 'Bulk actions'}
              </button>
              {bulkMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-2xl border border-gray-200 bg-white shadow-lg z-20">
                  <button
                    type="button"
                    onClick={() => handleBulkStatusChange('online')}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Mark as Online
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkStatusChange('offline')}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Mark as Offline
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkStatusChange('warning')}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Mark as Warning
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkStatusChange('error')}
                    className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Mark as Error
                  </button>
                  <div className="border-t border-gray-200" />
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                  >
                    Delete Devices
                  </button>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center rounded-full bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Export CSV
            </button>
          </div>
        </div>

        {selectedCount > 0 && (
          <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            {selectedCount} device{selectedCount === 1 ? '' : 's'} selected.
          </div>
        )}

        {actionFeedback && (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              actionFeedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-red-200 bg-red-50 text-red-600'
            }`}
          >
            {actionFeedback.message}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-4">
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Total Devices</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{totalDevices}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Online</p>
            <p className="mt-2 text-2xl font-semibold text-green-600">{onlineDevices}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Offline</p>
            <p className="mt-2 text-2xl font-semibold text-red-600">{offlineDevices}</p>
          </div>
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <p className="text-sm text-gray-500">Assigned</p>
            <p className="mt-2 text-2xl font-semibold text-gray-900">{assignedDevices}</p>
          </div>
        </div>

        <div className="rounded-3xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-6 py-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Search devices..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 md:w-64"
              />

              <Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
              >
                <option value="all">All statuses</option>
                <option value="online">Online</option>
                <option value="offline">Offline</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
              </Select>

              <Select
                value={organizationFilter}
                onChange={(event) => setOrganizationFilter(event.target.value)}
              >
                <option value="all">All organizations</option>
                {organizationOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>

              <Select
                value={deviceTypeFilter}
                onChange={(event) => setDeviceTypeFilter(event.target.value)}
              >
                <option value="all">All device types</option>
                {deviceTypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>

              <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={onlyAssigned}
                  onChange={(event) => setOnlyAssigned(event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                />
                Assigned only
              </label>
            </div>

            <div className="text-sm text-gray-500">
              Showing {filteredDevices.length} of {totalDevices} devices
              {selectedCount > 0 && (
                <span className="ml-2 font-medium text-gray-700">
                  {selectedCount} selected
                </span>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      checked={allVisibleSelected}
                      onChange={handleToggleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Device ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tickets</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Last Active</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {paginatedDevices.map(device => {
                  const isSelected = selectedDeviceIds.includes(device.id)
                  return (
                    <tr key={device.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-gray-50' : ''}`}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                          checked={isSelected}
                          onChange={() => handleToggleDeviceSelection(device.id)}
                        />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">{device.deviceId}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">{device.name}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{device.organization}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                            device.status === 'online'
                              ? 'bg-green-100 text-green-700'
                              : device.status === 'offline'
                              ? 'bg-red-100 text-red-700'
                              : device.status === 'warning'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <span
                            className={`mr-2 inline-block h-2 w-2 rounded-full ${
                              device.status === 'online'
                                ? 'bg-green-500'
                                : device.status === 'offline'
                                ? 'bg-red-500'
                                : device.status === 'warning'
                                ? 'bg-yellow-500'
                                : 'bg-gray-400'
                            }`}
                          />
                          {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{device.email}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{device.tickets}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{device.lastActive}</td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                        <button
                          onClick={() => handleViewDevice(device.id)}
                          className="rounded-full border border-gray-300 px-4 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {paginatedDevices.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-sm text-gray-500">
                      No devices match the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {filteredDevices.length > itemsPerPage && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredDevices.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
